import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Download, Smartphone } from 'lucide-react';

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [iosInfo, setIosInfo] = useState({ isIPad: false, inSafari: true });

  useEffect(() => {
    // Only prompt on phones/tablets/iPads (not desktop)
    const ua = navigator.userAgent;
    const isIOSDevice = (/iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && navigator.maxTouchPoints > 1)) && !window.MSStream;
    const isMobileOrTablet = isIOSDevice || /Android|Mobi|Tablet/i.test(ua) || navigator.maxTouchPoints > 1;
    setIsIOS(isIOSDevice);
    if (isIOSDevice) {
      const isIPad = /iPad/.test(ua) || (ua.includes('Mac') && navigator.maxTouchPoints > 1);
      const inSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|GSA|FBAN|FBAV|Instagram|WhatsApp|Line\//i.test(ua);
      setIosInfo({ isIPad, inSafari });
    }
    if (!isMobileOrTablet) return;

    // Snooze for 7 days after dismiss (instead of forever)
    const snoozedUntil = parseInt(localStorage.getItem('pwa-install-snooze') || '0', 10);
    const isSnoozed = Date.now() < snoozedUntil;

    // For iOS, show instructions if not in standalone mode
    if (isIOSDevice) {
      const isStandalone = window.navigator.standalone === true;
      if (!isStandalone && !isSnoozed) {
        setShowPrompt(true);
      }
      return;
    }

    // For Chrome/Android - handle beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (!isSnoozed) {
        setShowPrompt(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowPrompt(false);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.debug('User accepted the install prompt');
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-snooze', String(Date.now() + 7 * 24 * 3600 * 1000));
  };

  if (!showPrompt) return null;

  return (
    <div 
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-xl shadow-2xl p-4 z-50 animate-slide-up safe-area-bottom" 
      data-testid="install-prompt"
    >
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-slate-400 hover:text-white transition-colors p-1 rounded-full hover:bg-slate-700"
        data-testid="dismiss-install-prompt"
      >
        <X className="w-5 h-5" />
      </button>
      
      <div className="flex items-start gap-3 mb-4">
        <div className="bg-orange-500 p-2 rounded-lg flex-shrink-0">
          <Smartphone className="w-6 h-6 text-white" />
        </div>
        <div className="pr-6">
          <h3 className="font-bold text-lg mb-1">Install JMEPL Support</h3>
          <p className="text-sm text-slate-300 leading-relaxed">
            {isIOS 
              ? (iosInfo.isIPad ? "Follow the steps below to install the app on your iPad." : "Add to your home screen for quick access.")
              : "Install on your device for quick access. Works offline and loads faster!"
            }
          </p>
        </div>
      </div>
      
      {isIOS ? (
        <div className="bg-slate-700/50 rounded-lg p-3 text-sm space-y-2" data-testid="ios-install-steps">
          {!iosInfo.inSafari && (
            <p className="text-amber-300 font-medium" data-testid="ios-open-in-safari">
              Installation works only from the <b>Safari</b> browser. Copy this link, open it in Safari, then follow the steps below.
            </p>
          )}
          <div className="flex items-center gap-2 text-slate-200">
            <span>1. In Safari, tap the</span>
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 2a.75.75 0 01.75.75v5.19l1.72-1.72a.75.75 0 111.06 1.06l-3 3a.75.75 0 01-1.06 0l-3-3a.75.75 0 111.06-1.06l1.72 1.72V2.75A.75.75 0 0110 2z"/>
              <path d="M5.5 7.25a.75.75 0 011.5 0v7.5a.75.75 0 01-1.5 0v-7.5z"/>
              <path d="M13 14.25a.75.75 0 011.5 0v.5a.75.75 0 01-1.5 0v-.5z"/>
            </svg>
            <span>Share button {iosInfo.isIPad ? '(top, next to the address bar)' : '(bottom centre)'}</span>
          </div>
          <div className="text-slate-200">2. Scroll the list and choose <b>"Add to Home Screen"</b></div>
          <div className="text-slate-200">3. Tap <b>"Add"</b> at the top right — the app will appear on your Home Screen</div>
        </div>
      ) : (
        <Button
          onClick={handleInstall}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 text-base touch-target"
          data-testid="install-app-button"
        >
          <Download className="w-5 h-5 mr-2" />
          Install Now
        </Button>
      )}
    </div>
  );
};

export default InstallPrompt;
